import { useEffect, useState } from 'react';
import { Alert, Card, Descriptions, Form, Space, Tag, Typography } from 'antd';
import EntityStep from '../../../components/flow/EntityStep';
import hepatoRecordsApi, { type FollowUpStatus, type ScreeningSuggestion } from '../../../api/hepatoRecordsApi';
import { useHepatoWizard, useRegisterHepatoStepSave } from './HepatoWizardContext';
import HepatoAnamnesisFields from './HepatoAnamnesisFields';
import { loadAnamnesisSection, loadFamilySection, loadPersonalSection, loadSymptomsSection, saveAnamnesisSection } from './anamnesisFields';

const { Text } = Typography;

/** Pasul 1 — identificare (datele vin din înrolare; doar citire). */
export const HepatoStep1 = () => {
  const { patient, loading } = useHepatoWizard();
  if (loading || !patient) return <Card loading title="Identificare pacient" />;
  return (
    <Card title="Informații pacient">
      <Descriptions column={2} bordered size="small">
        <Descriptions.Item label="CNP">{patient.cnp}</Descriptions.Item>
        <Descriptions.Item label="Nume">
          {patient.lastName} {patient.firstName}
        </Descriptions.Item>
        <Descriptions.Item label="Data nașterii">{new Date(patient.dateOfBirth).toLocaleDateString('ro-RO')}</Descriptions.Item>
        <Descriptions.Item label="Sex">{patient.sex === 'M' ? 'Masculin' : 'Feminin'}</Descriptions.Item>
        <Descriptions.Item label="Telefon">{patient.phone ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Email">{patient.email ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Adresă" span={2}>
          {[patient.address, patient.city, patient.county].filter(Boolean).join(', ') || '—'}
        </Descriptions.Item>
      </Descriptions>
      <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
        Datele de identificare se completează la înrolare și nu se modifică din wizard.
      </Text>
    </Card>
  );
};

/** Pasul 2 — anamneză și factori de risc hepatici (JSONB pe formular). */
export const HepatoStep2Anamnesis = () => {
  const { form, patient, saveForm, loading } = useHepatoWizard();
  const [antForm] = Form.useForm();

  useEffect(() => {
    if (form) {
      antForm.setFieldsValue({
        generalData: form.generalData,
        currentSymptoms: loadSymptomsSection(form.currentSymptoms),
        personalHistory: loadPersonalSection(form.personalHistory),
        familyHistory: loadFamilySection(form.familyHistory),
        riskFactors: loadAnamnesisSection(form.riskFactors),
        previousExams: loadAnamnesisSection(form.previousExams),
        consent: form.consent,
      });
    }
  }, [form, antForm]);

  useRegisterHepatoStepSave(async () => {
    if (!form) return true;
    try {
      await antForm.validateFields();
    } catch {
      return false;
    }
    const v = antForm.getFieldsValue(true);
    const saved = await saveForm({
      generalData: v.generalData ?? {},
      currentSymptoms: v.currentSymptoms ?? {},
      personalHistory: { ...loadPersonalSection(v.personalHistory), oncologicHistory: v.personalHistory?.oncologicHistoryPresent ? v.personalHistory.oncologicHistory : '' },
      familyHistory: { ...(v.familyHistory ?? {}), otherChronicDiseases: v.familyHistory?.otherChronicDiseasesPresent ? v.familyHistory.otherChronicDiseases : '' },
      riskFactors: saveAnamnesisSection(v.riskFactors),
      previousExams: saveAnamnesisSection(v.previousExams),
      consent: v.consent ?? {},
    });
    return saved !== null;
  });

  if (loading || !form) return <Card loading title="Anamneză" />;

  return (
    <Card title="Anamneză și factori de risc">
      <Form form={antForm} layout="vertical" data-testid="hepato-anamnesis">
        <HepatoAnamnesisFields patient={patient} />
      </Form>
    </Card>
  );
};

const useFormId = () => {
  const { form, reloadFlow } = useHepatoWizard();
  return { formId: form?.id, reloadFlow };
};

const entityStep = (entity: string) => {
  const Step = () => {
    const { formId, reloadFlow } = useFormId();
    if (!formId) return <Card loading />;
    return <EntityStep formId={formId} entity={entity} onChanged={() => void reloadFlow()} />;
  };
  Step.displayName = `HepatoEntityStep(${entity})`;
  return Step;
};

export const HepatoStepAfp = entityStep('afp-tests');
export const HepatoStepUltrasound = entityStep('ultrasounds');
export const HepatoStepInvestigations = entityStep('investigations');
export const HepatoStepDiagnosis = entityStep('diagnosis');
export const HepatoStepTreatment = entityStep('treatments');
export const HepatoStepSurgery = entityStep('surgeries');
/** Pasul 11 — follow-up cu calendar: următorul control, întârziere și „lost to follow-up". */
export const HepatoStepFollowUp = () => {
  const { formId, reloadFlow } = useFormId();
  const [status, setStatus] = useState<FollowUpStatus | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!formId) return;
    hepatoRecordsApi.followUpStatus(formId).then(setStatus).catch(() => setStatus(null));
  }, [formId, version]);

  if (!formId) return <Card loading />;
  return (
    <EntityStep
      formId={formId}
      entity="follow-ups"
      onChanged={() => {
        setVersion((v) => v + 1);
        void reloadFlow();
      }}
      header={
        status?.nextVisitDate ? (
          <Alert
            style={{ marginBottom: 16 }}
            type={status.lostToFollowUp ? 'error' : status.overdue ? 'warning' : 'info'}
            showIcon
            data-testid="followup-status"
            message={
              status.lostToFollowUp
                ? `Pierdut din evidență (lost to follow-up): control restant de ${status.overdueDays} zile`
                : status.overdue
                  ? `Control restant de ${status.overdueDays} zile`
                  : `Următorul control: ${new Date(status.nextVisitDate).toLocaleDateString('ro-RO')}`
            }
            description={`Pragul „lost to follow-up": ${status.lostToFollowUpAfterDays} zile după data programată (propunere de validat medical).`}
          />
        ) : null
      }
    />
  );
};
export const HepatoStepOutcome = entityStep('outcome');

const SUGGESTION_LABELS: Record<ScreeningSuggestion['suggested'], { text: string; color: string }> = {
  POSITIVE: { text: 'Pozitiv', color: 'red' },
  NEGATIVE: { text: 'Negativ', color: 'green' },
  INCONCLUSIVE: { text: 'Neconcludent', color: 'orange' },
  INCOMPLETE: { text: 'Incomplet', color: 'default' },
};

/** Pasul 5 — sugestia calculată din AFP + ecografie; medicul confirmă mai jos. */
export const HepatoStepScreening = () => {
  const { formId, reloadFlow } = useFormId();
  const [suggestion, setSuggestion] = useState<ScreeningSuggestion | null>(null);

  useEffect(() => {
    if (!formId) return;
    hepatoRecordsApi.screeningSuggestion(formId).then(setSuggestion).catch(() => setSuggestion(null));
  }, [formId]);

  if (!formId) return <Card loading />;
  const label = suggestion ? SUGGESTION_LABELS[suggestion.suggested] : null;
  return (
    <EntityStep
      formId={formId}
      entity="screening-result"
      title="Rezultat screening"
      onChanged={() => void reloadFlow()}
      header={
        suggestion && label ? (
          <Alert
            style={{ marginBottom: 16 }}
            type={suggestion.suggested === 'POSITIVE' ? 'warning' : 'info'}
            showIcon
            message={
              <span data-testid="screening-suggestion">
                Sugestie calculată: <Tag color={label.color}>{label.text}</Tag>
              </span>
            }
            description={
              <>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {suggestion.reasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
                <Text type="secondary">
                  Prag AFP: {suggestion.afpThresholdNgMl} ng/mL (propunere de validat medical). Medicul confirmă concluzia mai jos.
                </Text>
              </>
            }
          />
        ) : null
      }
    />
  );
};

/** Pasul 7 — biopsie + histopatologie (două sub-sarcini, roluri diferite). */
export const HepatoStepBiopsyHp = () => {
  const { formId, reloadFlow } = useFormId();
  if (!formId) return <Card loading />;
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <EntityStep formId={formId} entity="biopsies" onChanged={() => void reloadFlow()} />
      <EntityStep formId={formId} entity="histopathology" onChanged={() => void reloadFlow()} />
    </Space>
  );
};

export const HEPATO_STEP_COMPONENTS = [
  HepatoStep1,
  HepatoStep2Anamnesis,
  HepatoStepAfp,
  HepatoStepUltrasound,
  HepatoStepScreening,
  HepatoStepInvestigations,
  HepatoStepBiopsyHp,
  HepatoStepDiagnosis,
  HepatoStepTreatment,
  HepatoStepSurgery,
  HepatoStepFollowUp,
  HepatoStepOutcome,
];

/** Index de wizard (0-based) → chei de pas din motorul de flux. */
export const HEPATO_STEP_KEYS: string[][] = [
  ['enrollment'],
  ['anamnesis'],
  ['afp'],
  ['ultrasound'],
  ['screeningResult'],
  ['investigations'],
  ['biopsy', 'histopathology'],
  ['diagnosis'],
  ['treatment'],
  ['surgery'],
  ['followUp'],
  ['outcome'],
];
